import { additionLimitMessage } from '../capacity'
import { Tooltip } from '@/components/ui/tooltip/Tooltip'
import { useRef, useState } from 'react'
import { Button } from '@/components/button/Button'
import { Upload } from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectRootFolderIdForMatter } from '@/user_files/folders/store/selectors'
import { useDriveUpload } from '@/user_files/drive/hooks/useDriveUpload'
import { DriveDropzone } from '@/user_files/drive/components/DriveDropzone'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import { ProcessingStatusItem } from '@/uppy-file-upload/components/ProcessingStatusItem'
import { useUserFilesPollingProcessor } from '@/user_files/polling/hooks/useUserFilesPollingProcessor'
import { tableUploadRegistered } from '../analysis/store/upload-actions'

/** The matter uploader owns bytes and progress; table listeners own durable attachments. */
export default function TableDocumentUploads({ matterId, tableId, remaining = Infinity }: { matterId: string; tableId: string; remaining?: number }) {
  const dispatch = useAppDispatch()
  const folderId = useAppSelector((state) => selectRootFolderIdForMatter(state, { matterId }))
  const attachedUploadIds = useAppSelector((state) => state.tableAnalysis.attachedUploadIds)
  const [limitError, setLimitError] = useState<string | undefined>()
  const accepting = useRef(false)
  const [uploadIds, setUploadIds] = useState<string[]>([])
  useUserFilesPollingProcessor()
  const upload = useDriveUpload({
    folderId,
    onFileRegistered: ({ clientTmpId }) => {
      setUploadIds((ids) => [...ids, clientTmpId])
      dispatch(tableUploadRegistered({ matterId, tableId, clientTmpId }))
    },
  })
  const addFiles = (files: File[]) => {
    if (accepting.current || upload.disabled || upload.isPreparingFiles) return
    const message = additionLimitMessage(remaining, files.length, 'documents')
    setLimitError(message)
    if (message) return
    accepting.current = true
    upload.addFiles(files)
    // The uploader's busy state is not visible until React rerenders. A synchronous reset
    // would let another same-tick pick enqueue the files again.
    queueMicrotask(() => {
      accepting.current = false
    })
  }
  const blockedMessage = additionLimitMessage(remaining, 1, 'documents')
  return (
    <div className="shrink-0 border-b border-brand-neutral-200 px-6 py-3">
      <DriveDropzone
        {...upload}
        disabled={upload.disabled || Boolean(blockedMessage)}
        addFiles={addFiles}
        onFilesPicked={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ''
          addFiles(files)
        }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <Tooltip title={blockedMessage ?? ''}>
            <span className="shrink-0" tabIndex={blockedMessage ? 0 : undefined}>
              <Button
                size="sm"
                variant="outlined"
                className="whitespace-nowrap"
                leadingIcon={<Upload />}
                disabled={upload.disabled || Boolean(blockedMessage)}
                onClick={upload.openUploadPicker}
              >
                Upload files
              </Button>
            </span>
          </Tooltip>
          <p className="min-w-0 flex-1 basis-48 text-xs text-brand-neutral-600">Or drop files here. Uploads are saved to this matter and added to the table.</p>
        </div>
        {limitError && (
          <p role="alert" className="mt-2 text-sm text-red-700">
            {limitError}
          </p>
        )}
        <div className="mt-2 max-h-48 space-y-2 overflow-auto" aria-label="Table uploads">
          {uploadIds.map((id) => (
            <ProcessingStatusItem
              key={id}
              clientTmpId={id}
              statusPrefix={attachedUploadIds.includes(id) ? 'Added to table' : undefined}
              onCancel={upload.cancelUpload}
              onDismiss={upload.removeFile}
            />
          ))}
        </div>
      </DriveDropzone>
      <ConflictModal {...upload.conflictDialog} operation="upload" />
    </div>
  )
}
